import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WestSussexCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="West Sussex Pothole Claims | Record Defects, 17% U-Roads RED | Fixtyer"
        description="West Sussex County Council recorded its highest-ever safety defects in 2024/25 and filled 144,779 potholes in four years — while 17% of unclassified roads are now in RED condition. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/west-sussex"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'West Sussex', url: '/councils/west-sussex' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'West Sussex spends well above its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN — West Sussex spent £43.1m of capital against a £25.866m DfT allocation in 2024/25 — but the ratings that matter for your claim are Condition and Best Practice, and both are AMBER. Section 58 of the Highways Act 1980 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend. A council that filled 44,699 potholes in a single year while recording its highest-ever safety defect count is, by its own numbers, running a network where defects outpace prevention.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in West Sussex?',
            answer: 'Unclassified roads make up 2,228km — 55% of West Sussex\'s 4,069km carriageway network — and 17% were in RED condition at the 2025 survey, the highest figure in five published years. The council surveys only 50% of U-roads each year via visual inspection (not SCANNER), meaning any given residential road may go a full year between condition surveys — in addition to the council\'s own admission that observed defects take at least two years to reach repair.',
          },
          {
            question: 'Does the council\'s extreme-weather explanation weaken my claim?',
            answer: 'No — it strengthens it in the right circumstances. West Sussex links the record 2024/25 safety defect count to named storms, intense rainfall and flooding. That is documented knowledge that the network is under elevated stress. If your pothole formed after a named storm or on a drainage-prone route, the council cannot simultaneously cite weather as the cause of record defects and argue it had no reason to inspect more frequently.',
          },
          {
            question: 'West Sussex says B and C roads are improving — does that hurt claims on those roads?',
            answer: 'Partially. RED-condition B/C roads fell from an 8% peak in 2022 to 5% in 2025, and the council attributes this to capital investment. Expect a stronger Section 58 defence on recently treated B/C corridors. But amber-condition B/C roads rose to 29% in 2025 — a third of the classified local network still needs attention — and the overall DfT Condition rating remains AMBER. Your claim still turns on the specific defect: prior reports, photos showing age, and whether that section had been surveyed recently.',
          },
          {
            question: 'Pothole repairs dipped from 48,104 to 44,699 in 2024/25 — are the roads fixed?',
            answer: 'No. West Sussex still repaired 44,699 potholes in 2024/25 — roughly 123 every day — and the council recorded the highest number of safety defects on record in the same year. The dip from 2023/24\'s peak of 48,104 is not evidence of recovery; reactive pothole work still accounted for 73% of repairs, and the council itself says defect numbers are rising year on year.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="West Sussex County Council" 
        councilSlug="west-sussex" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/west-sussex"
        headline="West Sussex Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">West Sussex</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            West Sussex: Record Defects on a Well-Funded Network
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            West Sussex spends well above its DfT capital allocation and earns a <strong className="text-emerald-400">GREEN</strong> spend 
            scorecard. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because safety defects hit 
            a <strong className="text-rose-400">record high in 2024/25</strong>, unclassified roads in RED condition have risen 
            to 17%, and the council filled <strong className="text-rose-400">144,779 potholes in four years</strong> while 
            admitting a two-year gap between survey and repair.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">17%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2025)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 8% in 2022 — the highest figure in five published years. On 2,228km of U-roads, 
              that is roughly 379km of residential and rural routes the council's own survey says need maintenance.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of SCANNER and visual survey data from West Sussex's own transparency report — 
            A-roads flat, B/C roads recovering from a 2022 spike, U-roads deteriorating
          </p>

          {/* Network size */}
          <div className="grid sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">4,069km</div>
              <div className="text-sm text-surface-600">Total carriageways</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">507km</div>
              <div className="text-sm text-surface-600">A-roads (12.5%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">1,334km</div>
              <div className="text-sm text-surface-600">B and C roads (32.8%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-amber-900">2,228km</div>
              <div className="text-sm text-amber-800">U-roads (54.8%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (507km — 12.5% of network): broadly stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold">6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">28%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has held at 6% since 2022. The council notes a slight improvement in 2025 
              where RED fell to 5.7% before rounding. Good-condition A-roads have drifted from 72% to 66% over 
              six years — not a crisis, but not improving either.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,334km — 32.8% of network): recovering from 2022 peak</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">69%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2025</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">29%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads hit 8% RED in 2022 — double the 2020 figure — before capital investment pulled RED 
              back to 5%. But amber-condition B/C roads are now at 29%, meaning roughly a third of classified 
              local roads are not in good condition.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Condition Still AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£25.9m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£43.1m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">70%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              West Sussex topped up DfT funding with <span className="text-emerald-400">£10.7m of corporate resources</span> for 
              its 2025/26 programme alone. The chequebook is not the problem — yet unclassified roads keep 
              deteriorating and safety defects keep breaking records.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Problem */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,228km U-Road Deterioration
          </h2>
          <p className="text-surface-600 mb-8">
            55% of West Sussex's carriageway network — and the condition trend the DfT AMBER rating reflects
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">12%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The proportion of the Unclassified Roads in 'RED' condition has risen by 2.9% to 16.6%, continuing an upward trend over the last 3 years. This is considered a reflection of the various pressures on the network, such as increasing volumes of utility works, and traffic as well as the impact of more extreme weather."
              source="West Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Visual Inspection, Not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                West Sussex runs annual SCANNER surveys on 100% of A and B roads and 50% of C roads. 
                Unclassified roads receive a <strong>visual inspection on 50% of the network each year</strong> — 
                a different, less granular method than the laser-and-camera surveys used elsewhere.
              </p>
              <p className="text-surface-600 text-sm">
                The council notes this is "in addition to safety inspections undertaken by WSCC officers 
                in accordance with the Highway Inspection Manual." Safety inspections and condition surveys 
                are not the same thing — and the council publishes condition data only from the surveys.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Lower Rural Standard</h3>
              <p className="text-amber-800 text-sm mb-3">
                West Sussex acknowledges that "much of the Unclassified network, particularly in rural areas, 
                is constructed to a lower standard" — and that neighbouring authorities show the same trend.
              </p>
              <p className="text-amber-800 text-sm">
                Lower construction standard does not eliminate the council's Section 41 duty to maintain 
                roads to a reasonable standard. It may affect what "reasonable" looks like — but 17% RED 
                means the council's own survey says maintenance is needed regardless.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, West Sussex must show it had a reasonable system for 
              knowing the condition of its roads. For the county's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 50% visually inspected this year, or the half that was not?</li>
              <li>• If 17% of U-roads are RED, what was done about the section where your pothole formed?</li>
              <li>• Does a visual inspection on alternate years provide adequate knowledge for Section 58?</li>
              <li>• The council cites utility works and extreme weather — did it increase inspections on affected routes?</li>
            </ul>
            <p className="text-white font-medium">
              A council that publishes rising RED percentages on 55% of its network cannot claim 
              ignorance of deteriorating local road conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Record Defects */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Record Safety Defects — 144,779 Potholes in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own revenue maintenance data — reactive work still dominates
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The highway network has been experiencing a notable, year-on-year increase in the number of safety defects, reaching the highest level on record in 2024/25. This rise is linked to a combination of an increase in named storm events and more frequent, intense and prolonged rainfall. This has led to a significant increase in flooding incidents and drainage-related issues across the network."
              source="West Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total potholes repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">22,728</td>
                  <td className="px-6 py-4 text-center">21,901</td>
                  <td className="px-6 py-4 text-center">827</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">96%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">29,248</td>
                  <td className="px-6 py-4 text-center">24,320</td>
                  <td className="px-6 py-4 text-center">4,928</td>
                  <td className="px-6 py-4 text-center">83%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">48,104</td>
                  <td className="px-6 py-4 text-center">29,660</td>
                  <td className="px-6 py-4 text-center">18,444</td>
                  <td className="px-6 py-4 text-center">62%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">44,699</td>
                  <td className="px-6 py-4 text-center">32,530</td>
                  <td className="px-6 py-4 text-center">12,169</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">73%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">144,779</td>
                  <td className="px-6 py-4 text-center font-bold">108,411</td>
                  <td className="px-6 py-4 text-center font-bold">36,368</td>
                  <td className="px-6 py-4 text-center font-bold">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~123 Potholes a Day in 2024/25</h3>
              <p className="text-surface-600 text-sm">
                West Sussex repaired 44,699 potholes in 2024/25 — roughly 123 every day — in the same year 
                it recorded its highest-ever safety defect count. Pothole numbers more than doubled from 
                22,728 in 2021/22 to a peak of 48,104 in 2023/24. A network producing defects at this 
                rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive Spend Still Rising</h3>
              <p className="text-amber-800 text-sm">
                Reactive pothole spend rose from £992,973 in 2021/22 to £1,887,231 in 2024/25 — nearly 
                double in three years. Preventative pothole spend also grew (to £1,784,669), but reactive 
                work still accounted for 73% of repairs in 2024/25 after briefly falling to 62% in 2023/24.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Two-Year Lag */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Two-Year Repair Lag
          </h2>
          <p className="text-surface-600 mb-8">
            West Sussex's own admission on how long survey findings take to become repairs
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The timeline for surveys, data analysis, design, procurement and delivery means observed road condition is unlikely to be addressed for at least 2 years, assuming sufficient budget is available."
              source="West Sussex County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means For Claims</h3>
              <p className="text-surface-600 text-sm mb-3">
                If a road was surveyed in summer 2024 showing RED condition, the council itself says 
                repair is unlikely before summer 2026 — even with budget available. A pothole that 
                damaged your car in between is exactly the gap Section 58 is designed to address.
              </p>
              <p className="text-surface-600 text-sm">
                The council's condition timeline diagram is based on the Summer 2024 survey. Ask whether 
                your road's section was flagged RED and what interim safety action was taken.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 From 2026/27</h3>
              <p className="text-amber-800 text-sm mb-3">
                West Sussex notes that from 2026/27 a new methodology based on the BSI PAS2161 standard 
                will replace the current three-category RCI system with five categories. Authorities will 
                need PAS2161-accredited survey suppliers.
              </p>
              <p className="text-amber-800 text-sm">
                Methodology changes can make year-on-year comparisons harder — as other councils have 
                found. Focus your claim on the specific defect and any prior reports, not network-wide trends alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending Deep Dive */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Six Years of Spending Data
          </h2>
          <p className="text-surface-600 mb-8">
            Capital investment rising steadily — the council tops up DfT funding every year since 2021/22
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Actual capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£31.1m</td>
                  <td className="px-4 py-3 text-center font-bold">£42.7m</td>
                  <td className="px-4 py-3 text-center">£21.33m</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">28%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£25.9m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£43.1m</td>
                  <td className="px-4 py-3 text-center">£20.62m</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£23.9m</td>
                  <td className="px-4 py-3 text-center">£38.8m</td>
                  <td className="px-4 py-3 text-center">£19.06m</td>
                  <td className="px-4 py-3 text-center">71%</td>
                  <td className="px-4 py-3 text-center">29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£20.9m</td>
                  <td className="px-4 py-3 text-center">£34.8m</td>
                  <td className="px-4 py-3 text-center">£13.32m</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£20.9m</td>
                  <td className="px-4 py-3 text-center">£29.5m</td>
                  <td className="px-4 py-3 text-center">£10.34m</td>
                  <td className="px-4 py-3 text-center">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£26.9m</td>
                  <td className="px-4 py-3 text-center">£20.1m</td>
                  <td className="px-4 py-3 text-center">£9.43m</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">216km Treated in 2024/25 — 246km Planned for 2025/26</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">77km</div>
                <div className="text-surface-600">Surface dressing 2024/25</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">49.5km</div>
                <div className="text-surface-600">Micro asphalt 2024/25</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">39.4km</div>
                <div className="text-surface-600">Resurfacing 2024/25</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">40.8km</div>
                <div className="text-surface-600">Heat-affected roads planned 2025/26</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              On a 4,069km network, 216km treated in a year is roughly 5.3% of carriageways receiving 
              capital treatment. The council notes 2025/26 projected figures may change with in-year priorities.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: West Sussex invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeded DfT allocation every year since 2021/22
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 70–78% of capital classed as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Proactive response — spray injection patchers, pothole "find and fix" gangs, increased drainage capacity
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C road RED condition improving from 8% peak in 2022 to 5% in 2025
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy aligned to UKRLG Code of Practice
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a organised Section 58 defence on recently treated B/C roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-roads in RED at 17%, highest in five published years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the GREEN asset-management scorecard some neighbours hold
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Record safety defects in 2024/25 — council's own words
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 144,779 potholes filled in four years — defects form faster than capital treatment covers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Two-year survey-to-repair lag admitted in the council's own report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 50% of U-roads visually inspected each year — not full SCANNER coverage
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and proactive maintenance gangs, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 17% RED trend and alternate-year visual survey gap are your strongest structural arguments</li>
              <li>• Timing relative to named storms — the council links record defects to extreme weather</li>
              <li>• Whether the road section was flagged RED in the last survey but not yet treated within the two-year window</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites West Sussex's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in West Sussex?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No two-year repair-lag citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 17% U-road RED trend documented</li>
                <li>✅ Record 2024/25 defect count cited</li>
                <li>✅ 144,779 repairs in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to West Sussex</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=west-sussex"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My West Sussex Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                West Sussex spends well above its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN — West Sussex spent £43.1m of capital against a 
                £25.866m DfT allocation in 2024/25 — but the ratings that matter for your claim are 
                Condition and Best Practice, and both are AMBER. Section 58 of the Highways Act 1980 turns 
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, 
                not on aggregate spend. A council that filled 44,699 potholes in a single year while recording 
                its highest-ever safety defect count is, by its own numbers, running a network where defects 
                outpace prevention.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in West Sussex?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 2,228km — 55% of West Sussex's 4,069km carriageway network — 
                and 17% were in RED condition at the 2025 survey, the highest figure in five published years. 
                The council surveys only 50% of U-roads each year via visual inspection (not SCANNER), meaning 
                any given residential road may go a full year between condition surveys — in addition to the 
                council's own admission that observed defects take at least two years to reach repair.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council's extreme-weather explanation weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it strengthens it in the right circumstances. West Sussex links the record 2024/25 safety 
                defect count to named storms, intense rainfall and flooding. That is documented knowledge that 
                the network is under elevated stress. If your pothole formed after a named storm or on a 
                drainage-prone route, the council cannot simultaneously cite weather as the cause of record 
                defects and argue it had no reason to inspect more frequently.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                West Sussex says B and C roads are improving — does that hurt claims on those roads?
              </h3>
              <p className="text-surface-600 text-sm">
                Partially. RED-condition B/C roads fell from an 8% peak in 2022 to 5% in 2025, and the council 
                attributes this to capital investment. Expect a stronger Section 58 defence on recently treated 
                B/C corridors. But amber-condition B/C roads rose to 29% in 2025 — a third of the classified 
                local network still needs attention — and the overall DfT Condition rating remains AMBER. Your 
                claim still turns on the specific defect: prior reports, photos showing age, and whether that 
                section had been surveyed recently.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs dipped from 48,104 to 44,699 in 2024/25 — are the roads fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. West Sussex still repaired 44,699 potholes in 2024/25 — roughly 123 every day — and the 
                council recorded the highest number of safety defects on record in the same year. The dip from 
                2023/24's peak of 48,104 is not evidence of recovery; reactive pothole work still accounted 
                for 73% of repairs, and the council itself says defect numbers are rising year on year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.westsussex.gov.uk/about-the-council/policies-and-reports/roads-and-travel-policy-and-reports/local-highway-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              West Sussex County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
